import React, { useState, useEffect } from "react";
import Loader from "../../components/Loader";
import { apiClient } from "../../apiclient/apiclient";
import { toast } from "react-toastify";
import AddLocationModal from "./components/AddLocation";
import { Pencil, Trash2 } from "lucide-react";
import ThemedTable from "../../components/ThemedTable";
import ConfirmDialog from "../../components/ConfirmDialog";

const Locations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [isAddLocationOpen, setIsAddLocationOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [locationName, setLocationName] = useState("");
  const [address, setAddress] = useState("");
  const [deleteLocationObj, setDeleteLocationObj] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const openEdit = (loc) => {
    setEditId(loc._id);
    setLocationName(loc.name);
    setAddress(loc.address);
    setIsAddLocationOpen(true);
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/locations");
      setLocations(response.data?.locations || []);
    } catch (err) {
      toast.error("Failed to fetch locations");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredLocations = locations.filter((loc) =>
    loc.name?.toLowerCase().includes(search.toLowerCase()) ||
    loc.address?.toLowerCase().includes(search.toLowerCase())
  );

  const handleDelete = async (id) => {
    setDeleting(true);
    try {
      await apiClient.delete(`/locations/${id}`);
      setLocations(locations.filter((l) => l._id !== id));
      toast.success("Location deleted successfully");
      setDeleteLocationObj(null);
    } catch (err) {
      toast.error("Failed to delete location");
    } finally {
      setDeleting(false);
    }
  };

  const handleAddLocation = async () => {
    if (!locationName.trim() || !address.trim()) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      setIsSaving(true); // ⭐ START LOADER

      const res = await apiClient.post("/locations/create", {
        name: locationName,
        address: address,
      });

      setLocations([...locations, res.data.location]);
      toast.success("Location added successfully");

      setLocationName("");
      setAddress("");
      setIsAddLocationOpen(false);

    } catch (err) {
      toast.error("Failed to add location");
    } finally {
      setIsSaving(false); // ⭐ STOP LOADER
    }
};
const handleUpdateLocation = async () => {
  if (!locationName.trim() || !address.trim()) {
    toast.error("Please fill all fields");
    return;
  }

  try {
    setIsSaving(true); // ⭐ START LOADER

    const res = await apiClient.put(`/locations/${editId}`, {
      name: locationName,
      address: address,
    });

    setLocations(
      locations.map((loc) =>
        loc._id === editId ? res.data.location : loc
      )
    );

    toast.success("Location updated successfully");

    setEditId(null);
    setLocationName("");
    setAddress("");
    setIsAddLocationOpen(false);

  } catch (err) {
    toast.error("Failed to update location");
  } finally {
    setIsSaving(false); // ⭐ STOP LOADER
  }
};


  return (
    <div>
      {/* HEADER - cloned from Brands page */}
      <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Locations</h1>
          <p className="text-sm text-slate-500 mt-1">Add, edit, or remove warehouse locations.</p>
        </div>
        <button
          onClick={() => setIsAddLocationOpen(true)}
          className="px-4 py-2 me-4 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors text-sm font-medium w-auto"
        >
          + Add Location
        </button>
      </div>

      {/* SEARCH - cloned from Brands page */}
      <div className="relative mb-6 max-w-md mx-auto">
        <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          {/* You may need to import Search from lucide-react if not already */}
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
        </span>
        <input
          type="text"
          placeholder="Search by name or address..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 pr-3 py-2 w-full text-sm bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition"
        />
      </div>

      {/* Table - cloned from All Admin table design */}
      <div className="bg-white p-0 sm:p-0 w-full overflow-x-auto">
        <ThemedTable className="text-left border-collapse w-full min-w-[700px]">
          <thead>
            <tr className="bg-white text-gray-700">
              <th className="p-3 whitespace-nowrap">Name</th>
              <th className="p-3 whitespace-nowrap">Address</th>
              <th className="p-3 whitespace-nowrap text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} className="py-10 text-center">
                  <Loader size={32} />
                </td>
              </tr>
            ) : filteredLocations.length > 0 ? (
              filteredLocations.map((loc) => (
                <tr key={loc._id} className="border-b hover:bg-gray-50">
                  <td className="p-3 whitespace-nowrap">{loc.name}</td>
                  <td className="p-3 whitespace-nowrap">{loc.address}</td>
                  <td className="p-3 whitespace-nowrap">
                    <div className="flex gap-2 justify-center">
                      <button
                        onClick={() => openEdit(loc)}
                        className="p-2 rounded-full border-2 border-[#172554] bg-[#172554] text-white hover:bg-white hover:text-[#172554] transition"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteLocationObj(loc)}
                        className="p-2 rounded-full border-2 border-orange-500 bg-orange-500 text-white hover:bg-white hover:text-orange-500 transition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className="px-6 py-4 text-center text-gray-500">
                  No locations found
                </td>
              </tr>
            )}
          </tbody>
        </ThemedTable>
      </div>

      {/* Add Location Modal */}
      <AddLocationModal
        show={isAddLocationOpen}
        onClose={() => {
          setIsAddLocationOpen(false);
          setEditId(null);
          setLocationName("");
          setAddress("");
        }}
        onSave={editId ? handleUpdateLocation : handleAddLocation}
        locationName={locationName}
        address={address}
        setLocationName={setLocationName}
        setAddress={setAddress}
        isEdit={!!editId}
        isSaving={isSaving}
      />

      {/* Delete Location Confirmation Modal */}
      <ConfirmDialog
        open={!!deleteLocationObj}
        title="Delete Location"
        description={`Are you sure you want to delete ${deleteLocationObj?.name}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={deleting}
        onCancel={() => setDeleteLocationObj(null)}
        onConfirm={() => handleDelete(deleteLocationObj._id)}
      />
    </div>
  );
};

export default Locations;
